import React from 'react';
import { connect } from 'react-redux';
import {
  Button, Card, CardBody, CardTitle,
} from 'reactstrap';
import { Form, actions } from 'react-redux-form/immutable';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import qs from 'qs';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { LabeledText } from '../../../../form/LabeledText';
import { LabeledSelect } from '../../../../form/LabeledSelect';
import { earthquakeSearchModel, toSearchParameters } from './earthquakeSearchModel';
import {
  searchEarthquakes as searchEarthquakesAc,
  clearEarthquakeSearch as clearEarthquakeSearchAc,
  getRegions as getRegionsAc,
  getCountries as getCountriesAc,
  getAreas as getAreasAc,
} from '../../../../../redux/earthquakes/search/earthquakeSearchActions';
import { Loading } from '../../../../loadbar/Loading';
import { isDataManager } from '../../../../../role/roleUtils';
import { withUser } from '../../../../auth/withUser';
import { LatLongSearchCard } from '../../../../form/search/LatLongSearchCard';
import { MinMaxSearchCard } from '../../../../form/search/MinMaxSearchCard';
import { TextMatchSearchCard } from '../../../../form/search/TextMatchSearchCard';
import { EffectsSearchCard } from '../../../../form/search/EffectsSearchCard';


const model = earthquakeSearchModel.get('name');

class EarthquakeSearchPageUnwrapped extends React.Component {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    document.title = 'NCEI Hazard Earthquake Search';
    const {
      history,
      clearEarthquakeSearch,
      getRegions,
      getCountries,
      getAreas,
      selectedCountry,
    } = this.props;
    clearEarthquakeSearch();
    getRegions({ history });
    getCountries({ history });
    if (selectedCountry) {
      getAreas({ country: selectedCountry, history });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      selectedCountry, change, selectedArea, getAreas, history,
    } = this.props;
    const { selectedCountry: prevSelectedCountry } = prevProps;
    if (prevSelectedCountry !== selectedCountry) {
      if (selectedArea) {
        change('.area', '');
      }
      if (selectedCountry) {
        getAreas({ country: selectedCountry, history });
      }
    }
  }

  handleSubmit(form) {
    const { searchEarthquakes, history } = this.props;
    const queryParameters = toSearchParameters(form);
    const location = {
      pathname: '/view/hazards/earthquake/event-data',
      search: qs.stringify(toSearchParameters(form), { skipNulls: true }),
    };
    searchEarthquakes({
      queryParameters, formModel: model, history, location, searchRedirect: true,
    });
  }

  render() {
    const {
      resetForm,
      searching,
      fetched,
      regions,
      countries,
      areas,
      fetchingAreas,
      peopleDescriptors,
      damageDescriptors,
      authorities,
    } = this.props;

    if (searching || !fetched) {
      return (<Loading />);
    }

    const peopleDescriptorsJs = peopleDescriptors.toJS();
    const damageDescriptorsJs = damageDescriptors.toJS();
    const regionsJs = regions.toJS().map(x => ({ id: x.id, description: `${(`000${x.id}`).slice(-3)} - ${x.description}` }));

    const dataManager = isDataManager(authorities);

    return (
      <div className="haz-results-container haz-page-container">

        <Card className="haz-page-card">
          <CardBody>
            <CardTitle tag="h1" className="haz-page-card-title">Search Earthquake Events</CardTitle>
            <p>
              The Significant Earthquake Database contains
              information on destructive earthquakes from 2150 B.C. to the
              present that meet at least one of the following criteria:
              Moderate damage (approximately $1 million or more), 10 or more
              deaths, Magnitude 7.5 or greater, Modified Mercalli Intensity
              X or greater, or the earthquake generated a tsunami.
              The database can also be displayed and extracted with the
              {' '}
              <a
                href="http://maps.ngdc.noaa.gov/viewers/hazards?layers=2&extent=-180,70,180,-70"
                rel="noopener noreferrer"
                target="_blank"
                title="Natural Hazards Viewer"
                aria-label="Natural Hazards Viewer"
              >
                Natural Hazards Interactive Map.
              </a>
            </p>
            <p>
              Please cite as:
              <br />
              {' '}
              <b>
                National Geophysical Data Center / World Data Service (NGDC/WDS): NCEI/WDS Global Significant Earthquake Database. NOAA National Centers for Environmental Information.
                {' '}
                <a href="http://dx.doi.org/10.7289/V5TD9V7K" rel="noopener noreferrer" target="_blank" title="Earthquake DOI Information" aria-label="Earthquake DOI Information">doi:10.7289/V5TD9V7K</a>
              </b>
            </p>
            <Form model={model} onSubmit={this.handleSubmit} noValidate>

              <div>
                <Button color="primary" type="submit" className="ml-5">Search</Button>
                <Button color="secondary" className="ml-3" onClick={resetForm}>Clear</Button>
              </div>
              <br />

              { dataManager && (
              <Card>
                <CardBody>
                  <CardTitle tag="h2">Earthquake ID</CardTitle>
                  <LabeledText model=".id" label="ID" />
                  <MinMaxSearchCard min=".minId" max=".maxId">ID Range</MinMaxSearchCard>
                </CardBody>
              </Card>
              )}

              <Card>
                <CardBody>
                  <CardTitle tag="h2">Earthquake Parameters</CardTitle>

                  {/* TODO set to correct modal ID */}
                  { dataManager && (
                  <div className="form-row">
                    <LabeledText formGroupClassName={['col-sm-6']} model=".month" label="Month" />
                    <LabeledText formGroupClassName={['col-sm-6']} model=".day" label="Day" />
                  </div>
                  )}
                  <MinMaxSearchCard min=".minYear" max=".maxYear" helpModalId="year">Year</MinMaxSearchCard>
                  <div className="form-row">
                    <LabeledSelect childOptions={regionsJs} formGroupClassName={['col-lg-4']} parentModel={model} fieldId="region" label="Region" filterable helpModalId="region" />
                    <LabeledSelect childOptions={countries.toJS()} formGroupClassName={['col-lg-6']} parentModel={model} fieldId="country" label="Country" filterable readOnly={fetchingAreas} helpModalId="eqCountry" />
                    <LabeledSelect childOptions={areas.toJS()} formGroupClassName={['col-lg-2']} parentModel={model} fieldId="area" label="Area" filterable helpModalId="eqArea" readOnly={fetchingAreas || !areas.size} />
                  </div>

                  <TextMatchSearchCard
                    radioGroupModel=".radios.locationSearchType"
                    textModel=".radios.loc"
                    startsWith="locStart"
                    includes="locInclude"
                    matches="locMatch"
                    doesNotMatch="locNot"
                    helpModalId="eqLocName"
                  >
                    Earthquake Location
                  </TextMatchSearchCard>

                  <LatLongSearchCard helpModalIdPrefix="eq" model={model} />

                  <div className="form-row row-margin-fix">
                    <MinMaxSearchCard className="col-lg-6" min=".minEqMagnitude" max=".maxEqMagnitude" helpModalId="eqMagnitude">Magnitude</MinMaxSearchCard>
                    <MinMaxSearchCard className="col-lg-6" min=".minIntensity" max=".maxIntensity" helpModalId="eqIntensity">Intensity</MinMaxSearchCard>
                  </div>

                  <MinMaxSearchCard min=".minEqDepth" max=".maxEqDepth" helpModalId="eqFocalDepth">Focal Depth (km)</MinMaxSearchCard>

                  <TextMatchSearchCard
                    radioGroupModel=".radios.commentsSearchType"
                    textModel=".radios.comments"
                    startsWith="commentsStart"
                    includes="commentsInclude"
                    matches="commentsMatch"
                    doesNotMatch="commentsNot"
                    helpModalId="eqComments"
                  >
                    Comments
                  </TextMatchSearchCard>

                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <CardTitle tag="h2">Hazard Association</CardTitle>
                  <div className="form-row">
                    <LabeledSelect
                      formGroupClassName={['col-md-6']}
                      parentModel={model}
                      fieldId="assocTsu"
                      label="Tsunami"
                      unselectedText="None Selected"
                      helpModalId="eqTsunami"
                      childOptions={[
                        { id: 'yes', description: 'Tsunami Was Associated' },
                        { id: 'no', description: 'Tsunami Was Not Associated' },
                      ]}
                    />
                    <LabeledSelect
                      formGroupClassName={['col-md-6']}
                      parentModel={model}
                      fieldId="assocVol"
                      label="Volcanic Eruption"
                      unselectedText="None Selected"
                      helpModalId="eqVolcano"
                      childOptions={[
                        { id: 'yes', description: 'Eruption Was Associated' },
                        { id: 'no', description: 'Eruption Was Not Associated' },
                      ]}
                    />
                  </div>
                </CardBody>
              </Card>

              <EffectsSearchCard helpModalIdPrefix="eq" deaths damage peopleDescriptors={peopleDescriptorsJs} model={model} damageDescriptors={damageDescriptorsJs}>Earthquake Effects</EffectsSearchCard>

              <EffectsSearchCard helpModalIdPrefix="eq" total deaths peopleDescriptors={peopleDescriptorsJs} model={model}>Total Earthquake and Secondary Effects</EffectsSearchCard>

              <br />
              <div>
                <Button color="primary" type="submit" aria-label="Search Option 2" className="ml-5">Search</Button>
                <Button color="secondary" className="ml-3" aria-label="Clear Option 2" onClick={resetForm}>Clear</Button>
              </div>

            </Form>
          </CardBody>
        </Card>
      </div>
    );
  }
}

EarthquakeSearchPageUnwrapped.propTypes = {
  searchEarthquakes: PropTypes.func.isRequired,
  clearEarthquakeSearch: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  searching: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  fetched: PropTypes.bool.isRequired,
  getRegions: PropTypes.func.isRequired,
  getCountries: PropTypes.func.isRequired,
  getAreas: PropTypes.func.isRequired,
  regions: ImmutablePropTypes.list.isRequired,
  countries: ImmutablePropTypes.list.isRequired,
  areas: ImmutablePropTypes.list.isRequired,
  fetchingAreas: PropTypes.bool.isRequired,
  selectedCountry: PropTypes.string.isRequired,
  selectedArea: PropTypes.string.isRequired,
  damageDescriptors: ImmutablePropTypes.list.isRequired,
  peopleDescriptors: ImmutablePropTypes.list.isRequired,
  history: PropTypes.shape({}).isRequired,
  authorities: ImmutablePropTypes.list.isRequired,
};

const mapStateToProps = state => ({
  fetched: [
    state.getIn(['earthquakeSearchPage', 'fetchedRegions']),
    state.getIn(['earthquakeSearchPage', 'fetchedCountries']),
  ].every(v => v),
  fetchingAreas: state.getIn(['earthquakeSearchPage', 'fetchingAreas']),
  regions: state.getIn(['earthquakeSearchPage', 'regions']),
  countries: state.getIn(['earthquakeSearchPage', 'countries']),
  areas: state.getIn(['earthquakeSearchPage', 'areas']),
  searching: state.getIn(['earthquakeSearchPage', 'searching']),
  selectedCountry: state.getIn([model, 'country']),
  selectedArea: state.getIn([model, 'area']),
  damageDescriptors: state.getIn(['effectDescriptors', 'damage']),
  peopleDescriptors: state.getIn(['effectDescriptors', 'people']),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  searchEarthquakes: searchEarthquakesAc,
  clearEarthquakeSearch: clearEarthquakeSearchAc,
  getRegions: getRegionsAc,
  getCountries: getCountriesAc,
  getAreas: getAreasAc,
  resetForm: () => actions.reset(model),
  change: (field, value) => actions.change(`${model}${field}`, value),
}, dispatch);

export const EarthquakeSearchPage = withUser(connect(mapStateToProps, mapDispatchToProps)(EarthquakeSearchPageUnwrapped));
